import { Box, Button, VStack, Text, Input, Switch, FormControl, HStack } from '@chakra-ui/react';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import DatePicker from 'react-multi-date-picker';
import gregorian_en from 'react-date-object/locales/gregorian_en';
import moment from 'moment';
import StyledInputWrapper from '../Forms/Forms.styled';
import '../Forms/formStyles.css';
import Label from '../Label';
import { DATE_SAVE_TO_DB_WITH_FORMAT, DATE_TO_SHOW_WITH_FORMAT } from '../../constants';
import { ModalComponent } from '../ui/Modals';

const getInitialFormValues = (date_format, startDate, existingData = null) => {
  let defaultDate = moment().format('YYYY-MM-DD');
  if (existingData?.start_date) {
    defaultDate = existingData.start_date;
  } else if (startDate) {
    const newStartDate = moment(startDate, 'YYYY-MM-DD').add(1, 'day').format('YYYY-MM-DD');
    defaultDate = newStartDate;
  }

  return {
    startDate: DATE_TO_SHOW_WITH_FORMAT(defaultDate, date_format),
    workingHours: existingData?.max_weekly_hours || '',
    multipleOccupancy: existingData?.house_occupancy_limit || '',
    consecutiveDays: existingData?.max_consecutive_days || '',
    duplicateBankAccounts:
      existingData && Object.keys(existingData).includes('duplication_bank_details')
        ? existingData.duplication_bank_details
        : true,
  };
};

// Create validation schema for compliance rule form
const ruleValidationSchema = Yup.object().shape({
  startDate: Yup.date().required('Start date is required').typeError('Please enter a valid date'),
  workingHours: Yup.number()
    .required('Working hours is required')
    .min(4, 'Working hours must be at least 4')
    .max(163, 'Working hours cannot exceed 163')
    .typeError('Please enter a valid number'),
  consecutiveDays: Yup.number()
    .required('Consecutive days is required')
    .min(2, 'Consecutive days must be at least 2')
    .max(365, 'Consecutive days cannot exceed 365')
    .typeError('Please enter a valid number'),
  duplicateBankAccounts: Yup.boolean().required('Duplicate bank accounts is required'),
  multipleOccupancy: Yup.number()
    .required('Multiple occupancy is required')
    .min(2, 'Multiple occupancy must be at least 2')
    .max(50, 'Multiple occupancy cannot exceed 50')
    .typeError('Please enter a valid number'),
});

const ComplianceRulesForm = function ({
  isOpen,
  onClose,
  onFormSubmit,
  isEditMode = false,
  existingData = null,
  defaultDate = null,
}) {
  const { user_details } = useSelector((state) => state.authentication);
  const startDateRef = useRef();
  const [startCalendarOpen, setStartCalendarOpen] = useState(false);

  const formik = useFormik({
    initialValues: getInitialFormValues(user_details.date_format, defaultDate, existingData),
    validationSchema: ruleValidationSchema,
    enableReinitialize: true,
    onSubmit: (values, { resetForm }) => {
      onFormSubmit(values);
      onClose();
      resetForm();
    },
  });
  const { values, errors, touched, handleChange, handleSubmit, resetForm } = formik;

  // Handle date picker change
  const handleDateChange = (date, fieldName) => {
    let newData = {};
    // Handle start date change
    if (date && fieldName === 'startDate') {
      const newStartDate = moment(date.toString(), user_details.date_format);
      // updating week start day and start date
      newData = {
        startDate: newStartDate.clone().format(user_details.date_format),
      };
    }

    formik.setValues((prev) => ({
      ...prev,
      ...newData,
    }));
  };

  // Handle click outside to close date picker in modal
  useEffect(() => {
    const handleClickOutside = (event) => {
      const { target } = event;

      // Check if click is outside start date picker and calendar is open
      if (startCalendarOpen && startDateRef.current) {
        const startDateElement =
          startDateRef.current.querySelector('.rmdp-input') || startDateRef.current;
        if (!startDateElement.contains(target)) {
          setStartCalendarOpen(false);
          // Try to close calendar using ref method
          if (startDateRef.current.closeCalendar) {
            startDateRef.current.closeCalendar();
          }
        }
      }
    };

    // Add event listener when modal is open
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    // Cleanup event listeners
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isOpen, startCalendarOpen]);

  // useMemo offer defaultDate update the the default date value with add 1 day
  const minStartDate = useMemo(() => {
    if (defaultDate) {
      const newStartDate = moment(defaultDate, 'YYYY-MM-DD').add(1, 'day').format('YYYY-MM-DD');
      return newStartDate;
    }
    return null;
  }, [defaultDate]);

  return (
    <ModalComponent
      isOpen={isOpen}
      onClose={() => {
        onClose();
        resetForm();
      }}
      modalTitle={isEditMode ? 'Edit Compliance Rule' : 'Add Compliance Rule'}
      specifySize="4xl"
      overflow="visible"
      modalContentStyle={{ zIndex: 1400 }}
      modalContent={
        <form onSubmit={handleSubmit} id="complianceRuleForm">
          <Box p={4}>
            <VStack spacing={4} align="stretch">
              {/* Date Range Section */}
              <HStack spacing={4} align="flex-start">
                <StyledInputWrapper>
                  <Label
                    label="Start Date*"
                    fontSize="md"
                    color="main.primary"
                    isError={errors.startDate && touched.startDate}
                  />
                  <DatePicker
                    ref={startDateRef}
                    locale={gregorian_en}
                    format={user_details.date_format}
                    inputClass={
                      // rulesExists && !isEditMode
                      isEditMode
                        ? 'date-picker-disabled'
                        : `date-picker ${
                            errors.startDate && touched.startDate ? 'input-error' : ''
                          }`
                    }
                    onChange={(date) => handleDateChange(date, 'startDate')}
                    value={values.startDate}
                    minDate={minStartDate ? DATE_SAVE_TO_DB_WITH_FORMAT(minStartDate) : null}
                    editable={false}
                    portal={false}
                    containerStyle={{
                      position: 'relative',
                    }}
                    onClose={() => {
                      setStartCalendarOpen(false);
                    }}
                    onOpen={() => {
                      setStartCalendarOpen(true);
                    }}
                    disabled={isEditMode}
                  />
                  {errors.startDate && touched.startDate && (
                    <Text color="red.500" fontSize="sm" mt={1}>
                      {errors.startDate}
                    </Text>
                  )}
                </StyledInputWrapper>
                <StyledInputWrapper>
                  <Label
                    label="Working Hours*"
                    isError={errors.workingHours && touched.workingHours}
                    fontSize="md"
                    color="main.primary"
                  />
                  <Input
                    focusBorderColor={
                      errors.workingHours && touched.workingHours ? 'red.400' : 'main.semiPrimary'
                    }
                    borderColor={
                      errors.workingHours && touched.workingHours ? 'red.400' : 'main.semiPrimary'
                    }
                    _hover={
                      errors.workingHours && touched.workingHours ? 'red.400' : 'main.semiPrimary'
                    }
                    color="main.black"
                    value={values.workingHours}
                    onChange={handleChange}
                    name="workingHours"
                    placeholder="Working hours per week"
                    fontSize="md"
                    type="number"
                  />
                  {errors.workingHours && touched.workingHours && (
                    <Text color="main.error" fontSize="sm" mt={1}>
                      {errors.workingHours}
                    </Text>
                  )}
                </StyledInputWrapper>
              </HStack>
              <HStack spacing={4} align="flex-start">
                <StyledInputWrapper>
                  <Label
                    label="House Occupancy*"
                    isError={errors.multipleOccupancy && touched.multipleOccupancy}
                    fontSize="md"
                    color="main.primary"
                  />
                  <Input
                    focusBorderColor={
                      errors.multipleOccupancy && touched.multipleOccupancy
                        ? 'red.400'
                        : 'main.semiPrimary'
                    }
                    borderColor={
                      errors.multipleOccupancy && touched.multipleOccupancy
                        ? 'red.400'
                        : 'main.semiPrimary'
                    }
                    _hover={
                      errors.multipleOccupancy && touched.multipleOccupancy
                        ? 'red.400'
                        : 'main.semiPrimary'
                    }
                    color="main.black"
                    value={values.multipleOccupancy}
                    onChange={handleChange}
                    name="multipleOccupancy"
                    placeholder="House occupancy"
                    fontSize="md"
                    type="number"
                  />
                  {errors.multipleOccupancy && touched.multipleOccupancy && (
                    <Text color="main.error" fontSize="sm" mt={1}>
                      {errors.multipleOccupancy}
                    </Text>
                  )}
                </StyledInputWrapper>
                <StyledInputWrapper>
                  <Label
                    label="Consecutive Days*"
                    isError={errors.consecutiveDays && touched.consecutiveDays}
                    fontSize="md"
                    color="main.primary"
                  />
                  <Input
                    focusBorderColor={
                      errors.consecutiveDays && touched.consecutiveDays
                        ? 'red.400'
                        : 'main.semiPrimary'
                    }
                    borderColor={
                      errors.consecutiveDays && touched.consecutiveDays
                        ? 'red.400'
                        : 'main.semiPrimary'
                    }
                    _hover={
                      errors.consecutiveDays && touched.consecutiveDays
                        ? 'red.400'
                        : 'main.semiPrimary'
                    }
                    color="main.black"
                    value={values.consecutiveDays}
                    onChange={handleChange}
                    name="consecutiveDays"
                    placeholder="Consecutive days"
                    fontSize="md"
                    type="number"
                  />
                  {errors.consecutiveDays && touched.consecutiveDays && (
                    <Text color="main.error" fontSize="sm" mt={1}>
                      {errors.consecutiveDays}
                    </Text>
                  )}
                </StyledInputWrapper>
                <Box width="100%">
                  <FormControl>
                    <Label
                      label="Duplicate Bank Accounts"
                      isError={errors.duplicateBankAccounts && touched.duplicateBankAccounts}
                      fontSize="md"
                      color="main.primary"
                    />
                    <Switch
                      id="duplicateBankAccounts"
                      name="duplicateBankAccounts"
                      isChecked={values.duplicateBankAccounts}
                      onChange={handleChange}
                    />
                  </FormControl>
                </Box>
              </HStack>
            </VStack>
          </Box>
        </form>
      }
      negative={
        <Button
          onClick={() => {
            onClose();
            resetForm();
          }}
          bg="main.secondary"
          mr={3}
          _hover={{ bg: 'main.secondary' }}
          color="main.black"
        >
          Cancel
        </Button>
      }
      positive={
        <Button
          type="submit"
          form="complianceRuleForm"
          bg="main.semiPrimary"
          color="main.white"
          _hover={{ bg: 'main.primary' }}
          _active={{
            bg: 'main.semiPrimary',
            borderColor: 'main.primary',
          }}
        >
          {isEditMode ? 'Update' : 'Save'}
        </Button>
      }
    />
  );
};

ComplianceRulesForm.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onFormSubmit: PropTypes.func.isRequired,
  isEditMode: PropTypes.bool,
  existingData: PropTypes.shape({
    startDate: PropTypes.string,
    workingHours: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    multipleOccupancy: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    consecutiveDays: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    duplicateBankAccounts: PropTypes.bool,
  }),
  defaultDate: PropTypes.string,
};

ComplianceRulesForm.defaultProps = {
  isEditMode: false,
  existingData: null,
  defaultDate: null,
};

export default ComplianceRulesForm;
